import { API } from "@/lib/api";
import { Kicker } from "@/components/landing/ui";
import { Scissors, SunDim, Footprints, Heart, Check, ArrowRight } from "lucide-react";

const PROBLEMS = [
  { icon: Scissors, text: "Scratches and surface wear" },
  { icon: SunDim, text: "Dull or tired-looking finish" },
  { icon: Footprints, text: "Scuffs and years of everyday use" },
  { icon: Heart, text: "Furniture you love that simply doesn't look its best anymore" },
];

export const ProblemSection = ({ beforePhotoId }) => (
  <section className="bg-black text-white py-20 sm:py-28" data-testid="problem-section">
    <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-12 gap-12 lg:gap-16">
      <div className="lg:col-span-5">
        <Kicker>The real problem</Kicker>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
          Your furniture may not be worn out.
          <br />
          <span className="text-[#c19a6b]">The finish may be.</span>
        </h2>
        {beforePhotoId && (
          <div className="relative mt-10 hidden lg:block">
            <img src={`${API}/photos/${beforePhotoId}`} alt="Worn furniture finish before Revival" loading="lazy" className="w-full aspect-[4/3] object-cover" />
            <span className="absolute bottom-3 left-3 bg-black/70 text-white text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5">Before</span>
          </div>
        )}
      </div>
      <div className="lg:col-span-7 lg:pt-16">
        <blockquote className="border-l-2 border-[#c19a6b] pl-6 sm:pl-8 text-white/80 text-lg leading-relaxed space-y-4">
          <p>A scratched, dull or tired finish can make beautiful furniture feel like it needs to be replaced.</p>
          <p>But if you still love the piece underneath, replacing the entire thing may solve a problem you don't actually have.</p>
        </blockquote>
        <div className="grid sm:grid-cols-2 gap-px bg-white/10 mt-12">
          {PROBLEMS.map(({ icon: Icon, text }) => (
            <div key={text} className="bg-black p-6 sm:p-7 flex items-start gap-4">
              <Icon size={20} className="text-[#c19a6b] shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-[15px] text-white/85 leading-snug">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const METHOD = [
  { n: "01", title: "Keep", text: "Preserve the existing wood colour, character and personality that made you love the piece in the first place." },
  { n: "02", title: "Revive", text: "Professionally prepare and renew the existing finish to dramatically improve its overall appearance." },
  { n: "03", title: "Protect", text: "Apply a fresh professional protective furniture finish so the piece is ready to be enjoyed again." },
];

export const MethodSection = () => (
  <section className="py-20 sm:py-28" data-testid="method-section">
    <div className="max-w-6xl mx-auto px-6">
      <div className="max-w-2xl">
        <Kicker>The ARK Revival method</Kicker>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
          Keep what you love.
          <br />
          Revive what you don't.
        </h2>
        <p className="text-gray-600 text-lg leading-relaxed mt-6">
          Revival is designed specifically for furniture where you already like the existing colour and character.
        </p>
      </div>
      <div className="grid md:grid-cols-3 gap-5 mt-14">
        {METHOD.map((m) => (
          <div key={m.n} className="group border border-gray-200 border-t-2 border-t-black hover:border-t-[#c19a6b] p-8 sm:p-9 transition-colors duration-200">
            <span className="font-display text-sm text-[#c19a6b] tracking-[0.2em]">{m.n}</span>
            <h3 className="font-display text-3xl tracking-[0.12em] uppercase mt-4">{m.title}</h3>
            <p className="text-gray-600 leading-relaxed mt-5 text-[15px]">{m.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PERFECT = [
  "You already like the current wood colour",
  "The furniture feels worth keeping",
  "The finish looks scratched, dull, scuffed or tired",
  "You want the piece refreshed and protected again",
  "You don't want to unnecessarily strip the entire piece",
];
const RESTORATION = [
  "You want a dramatically different wood colour",
  "The existing finish needs to be completely removed",
  "The piece has significant damage requiring restoration",
  "You want to fundamentally transform the furniture",
];

export const QualifySection = () => (
  <section className="bg-[#FAF7F2] py-20 sm:py-28" data-testid="qualify-section">
    <div className="max-w-6xl mx-auto px-6">
      <div className="max-w-2xl">
        <Kicker>Honest qualification</Kicker>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">Not every piece should be resurfaced.</h2>
        <p className="text-gray-600 text-lg leading-relaxed mt-6">That's exactly why we review your furniture before recommending the service.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-5 mt-14">
        <div className="bg-white border-t-4 border-[#c19a6b] p-8 sm:p-10 shadow-[0_20px_60px_rgba(0,0,0,0.05)]">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#c19a6b] font-bold">Revival may be perfect if:</p>
          <ul className="mt-7 space-y-4">
            {PERFECT.map((t) => (
              <li key={t} className="flex items-start gap-3 text-[15px] leading-snug">
                <Check size={18} className="text-[#c19a6b] shrink-0 mt-0.5" strokeWidth={2.5} />
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-black text-white p-8 sm:p-10">
          <p className="text-[11px] uppercase tracking-[0.25em] text-white/60 font-bold">A full restoration may be better if:</p>
          <ul className="mt-7 space-y-4">
            {RESTORATION.map((t) => (
              <li key={t} className="flex items-start gap-3 text-[15px] leading-snug text-white/85">
                <ArrowRight size={18} className="text-[#c19a6b] shrink-0 mt-0.5" />
                {t}
              </li>
            ))}
          </ul>
          <p className="text-white/50 text-sm leading-relaxed mt-8 italic font-display">
            Restoration is for furniture you want to change. Revival is for furniture you already love.
          </p>
        </div>
      </div>
    </div>
  </section>
);
